export type RequestType =
  | 'JISOMEE_METER'
  | 'WATER_TOKEN'
  | 'EXHAUSTER'
  | 'BOWSER'
  | 'SLUDGE'
  | 'METER_TEST'
  | 'METER_REPLACEMENT'
  | 'METER_SEPARATION'
  | 'NEW_CONNECTION'
  | 'CHANGE_OF_NAME'
  | 'CHANGE_OF_CONTACT'
  | 'INCIDENT_REPORT';

export type Status =
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'IN_PROGRESS'
  | 'APPROVED'
  | 'COMPLETED'
  | 'REJECTED'
  | 'CANCELLED';

export interface CustomerRequest {
  id: string;
  referenceNumber: string;
  requestType: RequestType;
  status: Status;
  /** Mobile-app user UUID — needed to attach the back-office chat. */
  userId?: string;
  accountNumber: string | null;
  accountName: string | null;
  contactName: string;
  contactMobile: string;
  contactEmail?: string;
  location: string | null;
  latitude?: number;
  longitude?: number;
  totalAmount: number | null;
  quantityValue?: number;
  quantityUnit?: string;
  reason?: string;
  description?: string;
  // Jisomee-specific reading details (only populated when the row exists).
  readingMeterNumber?: string;
  readingPrevious?: number;
  readingCurrent?: number;
  readingUnits?: number;
  readingType?: string;
  submittedAt: string; // ISO
  updatedAt: string;   // ISO
  assignee?: { id: string; name: string };
  photoUrls?: string[];
  /**
   * Supporting documents the applicant uploaded (ID, KRA PIN, application
   * form, water bill, tenancy agreement, …). Each carries the original
   * filename so we can pick the right MIME type and offer it as a
   * download — and for documents that are actually images we render them
   * the same way as photos.
   */
  documents?: RequestDocument[];
  notes?: Note[];
  activity?: ActivityEvent[];
}

export interface RequestDocument {
  /** Stable kind so the label stays consistent (e.g. "ID document"). */
  kind:
    | 'ID'
    | 'KRA_PIN'
    | 'ALT_ID'
    | 'APPLICATION_FORM'
    | 'WATER_BILL'
    | 'TENANCY_AGREEMENT';
  label: string;
  filename: string;
  /** data:<mime>;base64,<payload> — ready to drop into href / src. */
  dataUrl: string;
  /** True when filename ends with a common image extension. */
  isImage: boolean;
}

export interface Note {
  id: string;
  authorName: string;
  body: string;
  createdAt: string;
  internal: boolean;
}

export interface ActivityEvent {
  id: string;
  type: 'STATUS_CHANGE' | 'NOTE' | 'ASSIGNED' | 'MESSAGE_SENT' | 'CREATED';
  actorName: string;
  detail: string;
  createdAt: string;
}

export interface Customer {
  id: string;
  /** Primary water-services account number (c_acc_number on the legacy table). */
  accountNumber: string;
  name: string;
  phone: string;
  email?: string;
  /** Live balance via js_smart_revenue.get_c_acc_bal(c_acc_id). */
  balance: number;
  /** Human-readable status via get_account_status_dynamic_name(); free text. */
  statusLabel: string;
  // The fields below stay for the detail page / legacy UI; the list page no
  // longer renders them.
  verifiedAccountsCount: number;
  totalPaidKes: number;
  lastActiveAt: string;
  status: 'ACTIVE' | 'SUSPENDED';
  accounts?: { id: string; accountNumber: string; balance: number; location: string }[];
}

export interface Payment {
  id: string;
  referenceNumber: string;
  customer: { id: string; name: string };
  accountNumber: string;
  method: 'MPESA' | 'CARD' | 'BANK_TRANSFER';
  amount: number;
  status: 'SUCCESS' | 'FAILED' | 'PENDING' | 'CANCELLED';
  initiatedAt: string;
}

export interface Conversation {
  id: string;
  customer: { id: string; name: string; phone: string };
  requestRef?: string;
  lastMessagePreview: string;
  lastMessageAt: string;
  unreadCount: number;
  messages?: Message[];
}

export interface Message {
  id: string;
  conversationId: string;
  authorName: string;
  authorKind: 'CUSTOMER' | 'STAFF' | 'SYSTEM';
  body: string;
  internal: boolean;
  createdAt: string;
}

export interface NotificationLogEntry {
  id: string;
  sentAt: string;
  recipient: string;
  channel: 'PUSH' | 'SMS' | 'EMAIL';
  title: string;
  bodyPreview: string;
  status: 'DELIVERED' | 'FAILED' | 'PENDING';
}

export const REQUEST_TYPE_LABEL: Record<RequestType, string> = {
  JISOMEE_METER: 'Jisomee meter reading',
  WATER_TOKEN: 'Water token',
  EXHAUSTER: 'Exhauster',
  BOWSER: 'Bowser',
  SLUDGE: 'Sludge purchase',
  METER_TEST: 'Meter test',
  METER_REPLACEMENT: 'Meter replacement',
  METER_SEPARATION: 'Meter separation',
  NEW_CONNECTION: 'New connection',
  CHANGE_OF_NAME: 'Change of tenancy',
  CHANGE_OF_CONTACT: 'Change of contact',
  INCIDENT_REPORT: 'Incident report',
};

export const REQUEST_TYPE_PREFIX: Record<RequestType, string> = {
  JISOMEE_METER: 'JM',
  WATER_TOKEN: 'WT',
  EXHAUSTER: 'EX',
  BOWSER: 'BS',
  SLUDGE: 'SL',
  METER_TEST: 'MT',
  METER_REPLACEMENT: 'MR',
  METER_SEPARATION: 'MS',
  NEW_CONNECTION: 'NC',
  CHANGE_OF_NAME: 'CT',
  CHANGE_OF_CONTACT: 'CC',
  INCIDENT_REPORT: 'INC',
};
